import React, { useEffect, useRef, useState } from "react";
import { size } from "lodash";
import { usePrevious } from "react-use";
import { PopoverDisclosure, usePopoverState } from "reakit/Popover";
import styled from "styled-components";
import { PeriodRange } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import Loading from "@ui-kit/loading";
import Popover from "@ui-kit/popover";
import Recap from "@radarly/components/clause/labels/labels";
import Tooltip from "@radarly/components/clause/labels/tooltip";
import Button from "@radarly/components/filterbar/gigantic-button";
import { useLabelsFromClause } from "@radarly/core/clause/use-cases";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { Clause } from "@radarly/models/clause";
import { isClauseEmpty } from "@radarly/models/clause/is";
import { mergeClauses } from "@radarly/models/clause/merge";
import { getFacetFromField } from "@radarly/models/facets";
import Panel from "@radarly/shared/filterbar/panels";

interface IProps {
  field: Facet;
  clause: Clause;
  restClause: Clause;
  setClause: (clause: Clause) => void;
  onApply: () => void;
  onClose: () => void;
  label?: string;
  period: PeriodRange;
}

const LoaderContainer = styled.div`
  position: relative;
  width: 72px;
  padding: 3px 0 0 8px;
`;

const Loader = () => (
  <LoaderContainer>
    <Loading size="s" />
  </LoaderContainer>
);

const RadarlySharedFilterbarPermanent = ({
  field,
  clause,
  setClause,
  restClause,
  onApply,
  onClose,
  label,
  period,
}: IProps) => {
  const focusRef = useRef<HTMLSpanElement>(null);
  const facets = useFacetsFromContext();
  const facet = getFacetFromField(facets, field);
  const locale = useCurrentUserLocale();
  const popover = usePopoverState({ placement: "bottom-start" });
  const project = useCurrentProject();
  const [hasTitle, setHasTitle] = useState(!size(clause.and));
  const { loading, labels } = useLabelsFromClause(
    project.id,
    clause,
    facets,
    locale,
    period
  );
  const visible = usePrevious(popover.visible);
  useEffect(() => {
    if (!visible && popover.visible) {
      setHasTitle(!size(clause.and));
    }
    if (visible && !popover.visible) {
      onClose();
    }
  }, [clause.and, onClose, popover.visible, visible]);

  const onSetClause = (clause: Clause) => {
    // Do not keep empty clauses for permanent filters
    if (isClauseEmpty(clause)) {
      setClause(restClause);
    } else {
      setClause(mergeClauses(clause, restClause));
    }
  };
  const onApplyAndClose = () => {
    onApply();
    popover.hide();
  };
  return (
    <>
      {facet && (
        <>
          <span ref={focusRef} />
          <Tooltip
            disabled={labels.length < 1}
            locale={locale}
            title={facet.label}
            clausesLabels={labels}
          >
            <PopoverDisclosure
              {...popover}
              as={Button}
              disabled={loading}
              label={
                loading ? (
                  <Loader />
                ) : size(labels) ? (
                  <Recap clauseLabels={labels[0]} locale={locale} />
                ) : !!label ? (
                  label
                ) : (
                  facet.label
                )
              }
              maxWidth={size(labels) === 0 && !!label ? 9999 : undefined} // 9999 so label can take all the room it needs
              hasChevron
            />
          </Tooltip>
          <Popover
            popover={popover}
            ariaLabel="filter-selector"
            focusRef={focusRef}
          >
            {popover.visible ? (
              <Panel
                clause={clause}
                setClause={onSetClause}
                onApply={onApplyAndClose}
                facet={facet}
                hasTitle={hasTitle}
                visible={popover.visible}
                period={period}
              />
            ) : null}
          </Popover>
        </>
      )}
    </>
  );
};

export default RadarlySharedFilterbarPermanent;
